import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  callGetSignedVideoUrl,
  createDraftLesson,
  deleteLesson,
  getCourse,
  getLesson,
  getLessons,
  getProgress,
  getProgressForLessons,
  markLessonComplete,
  reorderLessons,
  unmarkLessonComplete,
  updateLesson,
  writeProgress,
  type Course,
  type Lesson,
  type Progress,
} from "../lib/firebase";
import { checkCourseAccess } from "../lib/entitlement";
import { parseVideoRef } from "../lib/videoRef";
import { BunnyPlayer } from "../components/BunnyPlayer";
import { FilePlayer } from "../components/FilePlayer";
import { YouTubePlayer } from "../components/YouTubePlayer";
import { CourseSidebar } from "../components/CourseSidebar";
import { LessonQuestions } from "../components/LessonQuestions";
import { CheckpointCard } from "../components/CheckpointCard";
import { LessonHomework } from "../components/HomeworkCard";
import { START_SET_BY_COURSE } from "../lib/checkpoints";
import { placeLesson, reorderWrites } from "../lib/lessonOrdering";
import { COMPLETE_RATIO, markWatched, watchedRatio } from "../lib/watchMap";
import { WatchHeatmap } from "../components/WatchHeatmap";

const PROGRESS_THROTTLE_MS = 30_000;

// Lesson body renderer (markdown + blocks): load it only when a lesson shows.
const LessonRenderer = lazy(() => import("../components/LessonRenderer").then((m) => ({ default: m.LessonRenderer })));
const AdminLessonEditPage = lazy(() => import("./admin/AdminLessonEditPage").then((m) => ({ default: m.AdminLessonEditPage })));

export function LessonPage() {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const { user, isAdmin, displayName, previewTier } = useAuth();
  const navigate = useNavigate();
  // Admin edits in place, Skool-style: ?edit=1 swaps the lesson for its editor.
  const [params, setParams] = useSearchParams();
  const editing = isAdmin && params.get("edit") === "1";
  const [reloadKey, setReloadKey] = useState(0);

  const [course, setCourse] = useState<Course | null>(null);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [siblings, setSiblings] = useState<Lesson[] | null>(null);
  const [progressMap, setProgressMap] = useState<Map<string, Progress>>(new Map());
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [resumeAt, setResumeAt] = useState<number>(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Watch map for this lesson + the last player position, to tell playback from seeks.
  const watchedRef = useRef("");
  const lastTickRef = useRef<number | null>(null);

  useEffect(() => {
    if (!courseId || !lessonId || !user) return;
    // Fully reset per-lesson state so a stale lesson body never lingers
    // between navigations. Also guards against an out-of-order Promise.all
    // resolution clobbering the freshly-clicked lesson by checking `cancelled`.
    let cancelled = false;
    setLesson(null);
    setAllowed(null);
    setVideoUrl(null);
    setVideoError(null);
    setResumeAt(0);
    setError(null);

    // Ask for the video link as soon as this one lesson doc says it is a Bunny
    // video, not after the rest of the page data. Locked lessons ignore the answer.
    const lessonReq = getLesson(courseId, lessonId);
    const signedVideo = lessonReq.then((l) =>
      parseVideoRef(l?.videoRef).kind === "bunny" ? callGetSignedVideoUrl(courseId, lessonId) : null
    );
    signedVideo.catch(() => {});

    Promise.all([
      getCourse(courseId),
      getLessons(courseId),
      lessonReq,
      checkCourseAccess(user.uid, courseId, { isAdmin, previewTier }),
      getProgress(user.uid, lessonId),
    ])
      .then(async ([c, all, lessonNow, decision, p]) => {
        if (cancelled) return;
        setCourse(c);
        const filtered = all.filter((l) => l.published || isAdmin);
        setSiblings(filtered);
        setLesson(lessonNow);
        setAllowed(decision.granted || (lessonNow?.isFree === true));
        setResumeAt(p?.lastPositionSec ?? 0);
        watchedRef.current = p?.watched ?? "";
        lastTickRef.current = null;

        // Sidebar ticks load alongside the video link, never in front of it.
        getProgressForLessons(user.uid, filtered.map((l) => l.id))
          .then((progress) => !cancelled && setProgressMap(progress))
          .catch(() => {});

        // Only Bunny videos need a server-signed URL. YouTube refs render from
        // the ref itself (see the render block below), so skip the call.
        const ref = parseVideoRef(lessonNow?.videoRef);
        if ((decision.granted || lessonNow?.isFree) && ref.kind === "bunny") {
          try {
            const signed = await signedVideo;
            if (cancelled) return;
            if (signed) setVideoUrl(signed.url);
          } catch (e) {
            if (cancelled) return;
            setVideoError(e instanceof Error ? e.message : String(e));
          }
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
      });

    return () => {
      cancelled = true;
    };
  }, [courseId, lessonId, user, isAdmin, previewTier, reloadKey]);

  if (!courseId || !lessonId) return <Navigate to="/dashboard" replace />;
  if (editing) {
    return (
      <Suspense fallback={<p style={{ padding: "3rem", color: "var(--c-text-muted)" }}>กำลังโหลด…</p>}>
      <AdminLessonEditPage
        key={lessonId}
        courseId={courseId}
        lessonId={lessonId}
        onDone={(deleted) => {
          if (deleted) navigate(`/courses/${courseId}`, { replace: true });
          else {
            setParams({});
            setReloadKey((k) => k + 1);
          }
        }}
      />
      </Suspense>
    );
  }
  if (error) return <main><p className="error">{error}</p></main>;
  if (allowed === false) return <Navigate to="/dashboard" replace />;
  if (!course || !lesson || !siblings || allowed === null)
    return <p style={{ padding: "3rem", color: "var(--c-text-muted)" }}>กำลังโหลด…</p>;

  const idx = siblings.findIndex((l) => l.id === lesson.id);
  const next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;

  async function handleCompleteAndContinue() {
    await handleMarkComplete();
    if (next) navigate(`/courses/${courseId}/lessons/${next.id}`);
  }

  async function handleMarkComplete() {
    if (!user) return;
    await markLessonComplete(user.uid, lesson!.id);
    setProgressMap((prev) => {
      const m = new Map(prev);
      m.set(lesson!.id, {
        userId: user!.uid,
        lessonId: lesson!.id,
        lastPositionSec: prev.get(lesson!.id)?.lastPositionSec ?? 0,
        completedAt: new Date(),
      });
      return m;
    });
  }

  async function handleUnmarkComplete() {
    if (!user) return;
    await unmarkLessonComplete(user.uid, lesson!.id);
    setProgressMap((prev) => {
      const m = new Map(prev);
      const existing = prev.get(lesson!.id);
      m.set(lesson!.id, {
        userId: user!.uid,
        lessonId: lesson!.id,
        lastPositionSec: existing?.lastPositionSec ?? 0,
        completedAt: null,
      });
      return m;
    });
  }

  async function addLesson() {
    const id = await createDraftLesson(courseId!);
    navigate(`/courses/${courseId}/lessons/${id}?edit=1`);
  }

  async function moveLesson(from: number, to: number, section?: string) {
    if (!siblings || to < 0 || to >= siblings.length) return;
    const moved = siblings[from];
    const { next, sectionTitle } = placeLesson(siblings, from, to, section);
    setSiblings(next.map((l, i) => ({ ...l, order: i + 1 })));
    try {
      await reorderLessons(courseId!, reorderWrites(next.map((l) => ({ id: l.id, order: l.order }))));
      if (sectionTitle !== (moved.sectionTitle ?? "").trim()) await updateLesson(courseId!, moved.id, { sectionTitle });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function renameLessons(ids: string[], patch: { title: string } | { sectionTitle: string }) {
    if (!siblings) return;
    setSiblings(siblings.map((l) => (ids.includes(l.id) ? { ...l, ...patch } : l)));
    if (ids.includes(lessonId!) && "title" in patch) setLesson((l) => (l ? { ...l, ...patch } : l));
    try {
      await Promise.all(ids.map((id) => updateLesson(courseId!, id, patch)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // Dew's call: deleting a lesson needs no confirm. A whole section asks (CourseSidebar).
  async function removeLessons(ids: string[]) {
    if (!siblings) return;
    const left = siblings.filter((l) => !ids.includes(l.id));
    setSiblings(left);
    try {
      await Promise.all(ids.map((id) => deleteLesson(courseId!, id)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    if (ids.includes(lessonId!)) {
      navigate(left.length ? `/courses/${courseId}/lessons/${left[0].id}` : `/courses/${courseId}`, { replace: true });
    }
  }

  const isCompleted = !!progressMap.get(lesson.id)?.completedAt;
  // เก็บความคืบหน้าเหมือนกันทั้งวิดีโอของ Bunny และไฟล์ที่เราโฮสต์เอง
  const handleVideoTick = (positionSec: number, durationSec: number) => {
    if (lastTickRef.current !== null) {
      watchedRef.current = markWatched(watchedRef.current, lastTickRef.current, positionSec, durationSec);
    }
    lastTickRef.current = positionSec;
  };
  // Complete = really played 80% of the clip (skipping to the end no longer counts).
  const handleVideoProgress = (positionSec: number, durationSec: number) => {
    const completed = watchedRatio(watchedRef.current, durationSec) >= COMPLETE_RATIO;
    if (user) writeProgress(user.uid, lesson.id, Math.floor(positionSec), completed, watchedRef.current);
    if (completed && !progressMap.get(lesson.id)?.completedAt) {
      setProgressMap((prev) => {
        const m = new Map(prev);
        m.set(lesson.id, {
          userId: user!.uid,
          lessonId: lesson.id,
          lastPositionSec: Math.floor(positionSec),
          completedAt: new Date(),
        });
        return m;
      });
    }
  };

  const videoInfo = parseVideoRef(lesson.videoRef);
  const videoKind = videoInfo.kind; // "none" | "youtube" | "bunny" | "file"

  return (
    <div className="course-shell">
      <CourseSidebar
        course={course}
        lessons={siblings}
        progress={progressMap}
        activeLessonId={lesson.id}
        admin={isAdmin ? { onAdd: addLesson, onMove: moveLesson, onDelete: removeLessons, onRename: renameLessons } : undefined}
      />

      <main className="course-shell__main">
        <article className="lesson-article">
          <header className="lesson-article__head">
            <h1 className="lesson-article__title">{lesson.title || "(บทยังไม่มีชื่อ)"}</h1>
            <button
              type="button"
              className={`lesson-icon-btn ${isCompleted ? "is-done" : ""}`}
              onClick={isCompleted ? handleUnmarkComplete : handleMarkComplete}
              aria-label={isCompleted ? "ยกเลิกเรียนจบ" : "ทำเครื่องหมายเรียนจบ"}
              title={isCompleted ? "เรียนจบแล้ว (กดเพื่อยกเลิก)" : "ทำเครื่องหมายเรียนจบ"}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M7.5 12.5l3 3 6-6.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            {isAdmin && (
              <button type="button" className="lesson-icon-btn" onClick={() => setParams({ edit: "1" })} aria-label="แก้ไขบทนี้" title="แก้ไขบทนี้">
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden><path d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
              </button>
            )}
          </header>
          {isAdmin && !lesson.published && (
            <div className="lesson-admin-bar">
              <span className="course-card__draft">ฉบับร่าง นักเรียนยังไม่เห็น</span>
            </div>
          )}

          {/* The start check-in is its own lesson (Dew, 2026-10-19): it shows where a lesson links the start set. */}
          {user && lesson.checkpointSetId && lesson.checkpointSetId === START_SET_BY_COURSE[courseId] && (
            <CheckpointCard userId={user.uid} courseId={courseId} setId={START_SET_BY_COURSE[courseId]} variant="start" />
          )}

          {videoKind === "youtube" && videoInfo.kind === "youtube" ? (
            // YouTube: no signed URL, no auto-progress. Student marks complete
            // with the button below.
            <YouTubePlayer videoId={videoInfo.id} />
          ) : videoInfo.kind === "file" ? (
            <FilePlayer
              src={videoInfo.url}
              resumeAt={resumeAt}
              throttleMs={PROGRESS_THROTTLE_MS}
              onProgress={handleVideoProgress}
              onTick={handleVideoTick}
            />
          ) : videoKind === "bunny" ? (
            videoUrl ? (
              <BunnyPlayer
                src={videoUrl}
                resumeAt={resumeAt}
                throttleMs={PROGRESS_THROTTLE_MS}
                onProgress={handleVideoProgress}
                onTick={handleVideoTick}
              />
            ) : videoError ? (
              <p className="error">โหลดวิดีโอไม่สำเร็จ: {videoError}</p>
            ) : (
              <p style={{ color: "var(--c-text-muted)" }}>กำลังเตรียมวิดีโอ…</p>
            )
          ) : null}

          {isAdmin && (videoKind === "file" || videoKind === "bunny") && <WatchHeatmap lessonId={lesson.id} />}

          <Suspense fallback={null}>
          <LessonRenderer
            lessonId={lesson.id}
            blocks={lesson.contentBlocks}
            markdownFallback={lesson.contentMarkdown}
          />
          </Suspense>

          {lesson.materials.length > 0 && (
            <section className="lesson-materials">
              <h2>เอกสารประกอบ</h2>
              <ul>
                {lesson.materials.map((m) => (
                  <li key={m.url}>
                    <a href={m.url} download>{m.name}</a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {user && lesson.checkpointSetId && lesson.checkpointSetId !== START_SET_BY_COURSE[courseId] && (
            <CheckpointCard userId={user.uid} courseId={courseId} setId={lesson.checkpointSetId} variant="lesson" />
          )}

          {user && lesson.homeworkId && (
            <LessonHomework userId={user.uid} courseId={courseId} homeworkId={lesson.homeworkId} isAdmin={isAdmin} />
          )}

          {user && (
            <LessonQuestions
              courseId={courseId}
              lessonId={lesson.id}
              user={{ uid: user.uid, email: user.email, displayName }}
              isAdmin={isAdmin}
            />
          )}
        </article>

        <footer className="lesson-cta">
          <Link to={`/courses/${courseId}`} className="btn-ghost">
            ดูภาพรวมคอร์ส
          </Link>

          {isCompleted ? (
            <div className="lesson-cta__group">
              <button onClick={handleUnmarkComplete} className="btn-secondary" type="button">
                ↺ ยกเลิกเรียนจบ
              </button>
              {next ? (
                <button
                  onClick={() => navigate(`/courses/${courseId}/lessons/${next.id}`)}
                  className="btn-primary"
                  type="button"
                >
                  ไปบทถัดไป →
                </button>
              ) : (
                <span className="lesson-cta__done-tag">✓ เรียนจบทุกบทแล้ว</span>
              )}
            </div>
          ) : (
            <button onClick={handleCompleteAndContinue} type="button">
              {next ? "Complete & Continue →" : "ทำเครื่องหมายเรียนจบ ✓"}
            </button>
          )}
        </footer>
      </main>
    </div>
  );
}
